import { useHistory, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import MediaQuery from 'react-responsive'
import { withStateHandlers } from 'recompose'
import classNames from 'classnames'

import { GRID_BREAKPOINT_MD } from 'shared/breakpoints'
import CloseButton from 'shared/components/CloseButton'
import ErrorMessage from 'shared/components/ErrorMessage'

import { authActiveFormSelector } from 'header/selectors'
import { ACTIVE_FORM_LOGIN, ACTIVE_FORM_SIGNUP } from 'header/constants'

import { CREATE_ACCOUNT_USER_EXISTS } from 'auth/constants'
import { resetState } from '../actions'
import * as constants from '../constants'
import { authStateSelector, loginErrorSelector, createAccountErrorSelector } from '../selectors'
import LoginForm from './LoginForm'
import SignupForm from './SignupForm'
import VehicleForm from './VehicleForm'
import CardForm from './CardForm'
import ForgotPasswordForm from './ForgotPasswordForm'
import SuccessView from './SuccessView'
import SuccessViewBR from './SuccessViewBR'
//TODO refactor global styles to modules
import '../styles/auth-container.scss'
import styles from './styles.module.scss'

const wrapWithError = (component, error) => (
    <div>
        {component}
        {error && <ErrorMessage messages={[error.message]} />}
    </div>
)

const $LoginSignupContainer = ({
    error,
    loginError,
    createAccountError,
    activeForm,
    authState,
    activateLogin,
    activateSignup,
}) => {
    const history = useHistory()
    const { hash } = useLocation()

    const changeUrlHash = newHash => history.push(newHash)
    const createAccountErrorMsg = classname => (
        <ErrorMessage messages={[<T>auth.sign.up.account.exists.error</T>]} className={classname} />
    )

    useEffect(() => {
        if (authState) {
            if (hash === '#login') {
                activateLogin()
            }
            if (hash === '#sign-up') {
                activateSignup()
            }
        }
    }, [hash, authState])

    const signUpFormError =
        createAccountError === CREATE_ACCOUNT_USER_EXISTS
            ? createAccountErrorMsg(styles.errorMessage)
            : error && <ErrorMessage messages={[error?.message]} className={styles.errorMessage} />

    return (
        <div>
            <MediaQuery minWidth={GRID_BREAKPOINT_MD}>
                <div className="header-user-container">
                    <LoginForm active={activeForm === ACTIVE_FORM_LOGIN} handleFocus={() => changeUrlHash('#login')} />
                    <SignupForm
                        active={activeForm === ACTIVE_FORM_SIGNUP}
                        handleFocus={() => changeUrlHash('#sign-up')}
                    />
                    {createAccountError === CREATE_ACCOUNT_USER_EXISTS
                        ? createAccountErrorMsg(styles.errorMessage)
                        : error && <ErrorMessage messages={[error?.message]} className={styles.errorMessage} />}
                </div>
            </MediaQuery>
            <MediaQuery maxWidth={GRID_BREAKPOINT_MD - 1}>
                <div className="header-user-container-responsive">
                    <LoginForm active={activeForm === ACTIVE_FORM_LOGIN} handleFocus={() => changeUrlHash('#login')} />
                    {loginError && <ErrorMessage messages={[loginError]} />}
                    <SignupForm
                        active={activeForm === ACTIVE_FORM_SIGNUP}
                        handleFocus={() => changeUrlHash('#sign-up')}
                        signUpFormError={signUpFormError}
                    />
                </div>
            </MediaQuery>
        </div>
    )
}
const LoginSignupContainer = compose(
    withTranslate,
    withStateHandlers(({ activeForm }) => ({ activeForm }), {
        activateLogin: () => () => ({
            activeForm: ACTIVE_FORM_LOGIN,
        }),
        activateSignup: () => () => ({
            activeForm: ACTIVE_FORM_SIGNUP,
        }),
    })
)($LoginSignupContainer)

const AuthContainer = ({ auth, closeAuth, resetState, loginError, createAccountError, activeForm }) => {
    const history = useHistory()

    const resetAndClose = () => {
        if (closeAuth) closeAuth()
        if (resetState) resetState()
    }
    const getContent = (state, error, loginError, createAccountError, activeForm) => {
        switch (state) {
            case constants.AUTH_CONTAINER_STATE_VEHICLES:
                return wrapWithError(<VehicleForm />, error)
            case constants.AUTH_CONTAINER_STATE_CARDS:
                return wrapWithError(<CardForm />, error)
            case constants.AUTH_CONTAINER_STATE_SUCCESS:
                return wrapWithError(<SuccessView resetAndClose={resetAndClose} />, error)
            case constants.AUTH_CONTAINER_STATE_ACTIVATION:
                return wrapWithError(<SuccessViewBR />, error)
            case constants.AUTH_CONTAINER_STATE_FORGOT_PASSWORD:
                return wrapWithError(
                    <ForgotPasswordForm isPasswordResetEmailSent={auth.isPasswordResetEmailSent} />,
                    error
                )
            case constants.AUTH_CONTAINER_STATE_MAIN:
            default:
                return (
                    <LoginSignupContainer
                        activeForm={activeForm}
                        error={error}
                        loginError={loginError}
                        createAccountError={createAccountError}
                        authState={auth}
                    />
                )
        }
    }

    const getCloseButtonAction = state => {
        switch (state) {
            case constants.AUTH_CONTAINER_STATE_VEHICLES:
            case constants.AUTH_CONTAINER_STATE_CARDS:
            case constants.AUTH_CONTAINER_STATE_SUCCESS:
                return resetAndClose
            case constants.AUTH_CONTAINER_STATE_FORGOT_PASSWORD:
                return resetState
            case constants.AUTH_CONTAINER_STATE_MAIN:
            default:
                return closeAuth
        }
    }
    return (
        <>
            {getContent(auth.state, auth.error, loginError, createAccountError, activeForm)}
            {closeAuth && (
                <div className={classNames('container', styles.closeBtn)}>
                    <CloseButton
                        action={() => {
                            getCloseButtonAction(auth.state), history.push({ hash: false }), closeAuth()
                        }}
                    />
                </div>
            )}
        </>
    )
}

const mapStateToProps = createStructuredSelector({
    auth: authStateSelector,
    loginError: loginErrorSelector,
    createAccountError: createAccountErrorSelector,
    activeForm: authActiveFormSelector,
})
const mapDispatchToProps = {
    resetState,
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslate)(AuthContainer)
